// src/pages/VoiceRecorderPage.jsx
import React, { useEffect, useRef, useState } from "react";
import "./SpeechRecognitionpage.css";
import KeyboardVoiceIcon from "@mui/icons-material/KeyboardVoice";
import StopCircleIcon from "@mui/icons-material/StopCircle";

const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;

function VoiceRecorderPage() {
  const [isRecording, setIsRecording] = useState(false);
  const [lang, setLang] = useState("or-IN"); // default to Odia
  const [interimTranscript, setInterimTranscript] = useState("");
  const [finalTranscript, setFinalTranscript] = useState("");
  const [translatedText, setTranslatedText] = useState("");
  const [storedNotes, setStoredNotes] = useState([]);
  const recognitionRef = useRef(null);

  useEffect(() => {
    if (!SpeechRecognitionAPI) {
      console.warn("Web Speech API not supported in this browser.");
      return;
    }

    const recognition = new SpeechRecognitionAPI();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = lang;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      console.log("Mic started");
    };

    recognition.onerror = (event) => {
      console.error("Speech recognition error:", event);
    };

    recognition.onresult = (event) => {
      // Build transcripts
      let interim = "";
      let final = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i];
        if (res.isFinal) {
          final += res[0].transcript;
        } else {
          interim += res[0].transcript;
        }
      }

      if (interim) setInterimTranscript(interim);
      if (final) {
        setFinalTranscript((prev) => (prev ? prev + " " + final : final));
        setInterimTranscript("");
      }
    };

    recognitionRef.current = recognition;

    return () => {
      // cleanup
      recognition.onresult = null;
      recognition.onend = null;
      recognition.onerror = null;
      recognition.onstart = null;
      recognitionRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => {
    const rec = recognitionRef.current;
    if (!rec) return;

    if (isRecording) {
      try {
        rec.lang = lang;
        rec.start();
        // When `onend` triggers, restart automatically while isRecording
        rec.onend = () => {
          if (isRecording) {
            // some browsers require a small delay before restart
            setTimeout(() => {
              try { rec.start(); } catch (e) {}
            }, 250);
          }
        };
      } catch (e) {
        console.error("Start error:", e);
      }
    } else {
      try {
        rec.onend = () => console.log("Stopped by user");
        rec.stop();
      } catch (e) {
        console.warn("Stop error:", e);
      }
    }
  }, [isRecording, lang]);

  const handleTranslate = async (textToTranslate) => {
    if (!textToTranslate) return;
    try {
      const res = await fetch("/api/translate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: textToTranslate, source: lang.startsWith("or") ? "or" : "auto", target: "en" }),
      });
      const data = await res.json();
      if (data && data.translatedText) {
        setTranslatedText(data.translatedText);
      } else {
        setTranslatedText("[Translation failed]");
      }
    } catch (err) {
      console.error("Translate error:", err);
      setTranslatedText("[Translation error]");
    }
  };

  // Save note (both original Odia text and translated English)
  const handleSaveNote = async () => {
    const note = {
      original: finalTranscript.trim() || interimTranscript.trim(),
      translated: translatedText,
      lang,
      createdAt: new Date().toISOString(),
    };
    try {
      const res = await fetch("/api/notes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(note),
      });
      const saved = await res.json();
      setStoredNotes((prev) => [saved, ...prev]);
      // clear current transcripts
      setFinalTranscript("");
      setInterimTranscript("");
      setTranslatedText("");
    } catch (err) {
      console.error("Save note error:", err);
    }
  };

  const loadNotes = async () => {
    try {
      const res = await fetch("/api/notes");
      const data = await res.json();
      setStoredNotes(data || []);
    } catch (err) {
      console.error("Load notes error:", err);
    }
  };

  useEffect(() => {
    loadNotes();
  }, []);

  return (
    <div className="recorder-container">
      <div className="recorder-box">
        <h2>Voice Recorder — Odia → English</h2>

        <div style={{ marginBottom: 12 }}>
          <label>
            Choose language:{" "}
            <select value={lang} onChange={(e) => setLang(e.target.value)}>
              <option value="or-IN">Odia (ଓଡ଼ିଆ) — or-IN</option>
              <option value="en-US">English — en-US</option>
              <option value="auto">Auto (browser default)</option>
            </select>
          </label>
        </div>

        <div className="recorder-buttons">
          <button
            className={`mic-toggle-btn ${isRecording ? "recording" : ""}`}
            onClick={() => setIsRecording((prev) => !prev)}
            title={isRecording ? "Stop recording" : "Start recording"}
          >
            {isRecording ? (
              <StopCircleIcon style={{ fontSize: "2.2rem" }} />
            ) : (
              <KeyboardVoiceIcon style={{ fontSize: "2.2rem" }} />
            )}
          </button>

          <button
            className="translate-btn"
            onClick={() => handleTranslate(finalTranscript || interimTranscript)}
          >
            Translate to English
          </button>

          <button className="save-btn" onClick={handleSaveNote}>
            Save note
          </button>
        </div>

        <div className="transcript-area">
          <h4>Odia (live)</h4>
          <p className="live-text">{finalTranscript} <em>{interimTranscript}</em></p>

          <h4>Translated English</h4>
          <p className="translated-text">{translatedText}</p>
        </div>

        <div className="notes-list">
          <h4>Saved notes</h4>
          {storedNotes.length === 0 && <p>No notes saved yet.</p>}
          <ul>
            {storedNotes.map((n, i) => (
              <li key={i}>
                <div><strong>Original ({n.lang}):</strong> {n.original}</div>
                <div><strong>English:</strong> {n.translated}</div>
                <div style={{ fontSize: 12, color: "#666" }}>{new Date(n.createdAt).toLocaleString()}</div>
                <hr />
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default VoiceRecorderPage;
